/**
 * Finance Feature React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import * as api from '@/api/queries/finanze';
import type { Transazione, TransazioneInsert, TransazioneUpdate, FinanzeSummary, CategoriaStats, TrendMensile } from './types';

const QUERY_KEY = 'transazioni';

// ============================================================
// QUERIES
// ============================================================

export function useTransazioni(periodo?: { inizio: Date; fine: Date }) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'list', user?.id, periodo],
    queryFn: async () => {
      if (periodo) {
        const { data, error } = await api.getTransazioniInPeriodo(periodo.inizio, periodo.fine, user?.id);
        if (error) throw error;
        return (data || []) as Transazione[];
      }
      const { data, error } = await api.getTransazioni({ userId: user?.id });
      if (error) throw error;
      return (data || []) as Transazione[];
    },
    enabled: !!user?.id,
  });
}

export function useTransazione(id?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, id, user?.id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api.getTransazioneById(id, user?.id);
      if (error) throw error;
      return data as Transazione | null;
    },
    enabled: !!id && !!user?.id,
  });
}

export function useFinanzeSummary() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'summary', user?.id],
    queryFn: async () => {
      const { data, error } = await api.getFinanzeSummary(user?.id);
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

export function useCategorieStats(periodo?: { inizio: Date; fine: Date }) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'categorie', user?.id, periodo],
    queryFn: async () => {
      const { data, error } = await api.getCategorieStats(periodo?.inizio, periodo?.fine, user?.id);
      if (error) throw error;
      return (data || []) as CategoriaStats[];
    },
    enabled: !!user?.id,
  });
}

export function useTrendMensile(anni: number = 1) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'trend', anni, user?.id],
    queryFn: async () => {
      const { data, error } = await api.getTrendMensile(anni, user?.id);
      if (error) throw error;
      return (data || []) as TrendMensile[];
    },
    enabled: !!user?.id,
  });
}

// ============================================================
// MUTATIONS
// ============================================================

export function useCreateTransazione() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (transazione: TransazioneInsert) => {
      const { data, error } = await api.createTransazione(transazione, user?.id);
      if (error) throw error;
      return data as Transazione;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateTransazione() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: TransazioneUpdate }) => {
      const { data: result, error } = await api.updateTransazione(id, data, user?.id);
      if (error) throw error;
      return result as Transazione;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
    },
  });
}

export function useDeleteTransazione() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.deleteTransazione(id, user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useTransazioniStats() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', user?.id],
    queryFn: async () => {
      const { data, error } = await api.getTransazioni({ userId: user?.id });
      if (error) throw error;
      const transazioni = (data || []) as Transazione[];
      
      const entrate = transazioni.filter(t => t.tipo === 'entrata');
      const uscite = transazioni.filter(t => t.tipo === 'uscita');
      
      return {
        totali: transazioni.length,
        entrate: entrate.length,
        uscite: uscite.length,
        totale_entrate: entrate.reduce((sum, t) => sum + (t.importo || 0), 0),
        totale_uscite: uscite.reduce((sum, t) => sum + (t.importo || 0), 0),
        saldo: entrate.reduce((sum, t) => sum + (t.importo || 0), 0) - uscite.reduce((sum, t) => sum + (t.importo || 0), 0),
      };
    },
    enabled: !!user?.id,
  });
}
